import type { DashboardSnapshot } from '@/data/types'
import type { MetricKey, MilestoneDefinition } from './types'

function readMetric(snapshot: DashboardSnapshot, key: MetricKey): number {
  switch (key) {
    case 'clients.active':
      return snapshot.clients.active
    case 'clients.newThisMonth':
      return snapshot.clients.newThisMonth
    case 'revenue.mrrCzk':
      return snapshot.revenue.mrrCzk ?? 0
    case 'incidents.open':
      return snapshot.incidents.open
    case 'incidents.closedToday':
      return snapshot.incidents.closedToday
    case 'incidents.closedTotal':
      return snapshot.incidents.closedTotal
    case 'support.mttrMin':
      return snapshot.incidents.mttrMin
    case 'support.csat':
      return snapshot.support.csat
    case 'support.nps':
      return snapshot.support.nps
    case 'churn.ratePct':
      return snapshot.churn.ratePct
    case 'reliability.daysWithoutP1':
      return snapshot.incidents.daysWithoutP1
  }
}

export function metricValue(snapshot: DashboardSnapshot, key: MetricKey): number {
  return readMetric(snapshot, key)
}

export function isMilestoneReached(def: MilestoneDefinition, snapshot: DashboardSnapshot): boolean {
  const v = readMetric(snapshot, def.metric)
  return def.direction === 'gte' ? v >= def.threshold : v <= def.threshold
}

export function detectMilestones(
  defs: MilestoneDefinition[],
  prev: DashboardSnapshot | null,
  next: DashboardSnapshot,
  already: Set<string>,
): string[] {
  const newlyHit: string[] = []
  for (const def of defs) {
    if (def.oneShot && already.has(def.id)) continue
    const reachedNow = isMilestoneReached(def, next)
    if (!reachedNow) continue
    if (prev) {
      const reachedBefore = isMilestoneReached(def, prev)
      if (def.oneShot && reachedBefore) continue
      if (!def.oneShot && reachedBefore) continue
    }
    newlyHit.push(def.id)
  }
  const scaleOrder: Record<string, number> = { mini: 0, mid: 1, major: 2, epoch: 3 }
  const tierOrder: Record<string, number> = { bronze: 0, silver: 1, gold: 2, platinum: 3 }
  newlyHit.sort((a, b) => {
    const da = defs.find((d) => d.id === a)!
    const db = defs.find((d) => d.id === b)!
    if (da.category !== db.category) return da.category.localeCompare(db.category)
    if (scaleOrder[da.scale] !== scaleOrder[db.scale])
      return scaleOrder[da.scale] - scaleOrder[db.scale]
    if (tierOrder[da.tier] !== tierOrder[db.tier]) return tierOrder[da.tier] - tierOrder[db.tier]
    return da.threshold - db.threshold
  })
  return newlyHit
}
